import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CarrierFormDialog from "@/components/CarrierFormDialog";

export default function CarriersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/carriers", { params: q ? { q } : {} });
      setItems(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const del = async (id, name = "this carrier") => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await api.delete(`/carriers/${id}`);
      toast.success("Carrier deleted");
      load();
      return;
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail) || "Delete failed";
      if (status !== 409) {
        toast.error(detail);
        return;
      }
      const forceOk = window.confirm(
        `${detail}\n\nUnlink from those jobs and delete the carrier anyway? (The jobs will remain, but their carrier will be cleared.)`
      );
      if (!forceOk) return;
      try {
        const { data } = await api.delete(`/carriers/${id}?force=true`);
        toast.success(`Carrier deleted (unlinked from ${data?.jobs_unlinked || 0} job(s))`);
        load();
      } catch (err2) {
        toast.error(formatApiError(err2.response?.data?.detail) || "Delete failed");
      }
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <div className="mono-label">DIRECTORY</div>
          <h2 className="text-2xl font-bold tracking-tight mt-1">Carriers</h2>
          <p className="text-sm text-zinc-600">Saved carrier partners. Reuse them on rate confirmations.</p>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, MC#, DOT#..." className="sharp apex-input h-10 pl-9 w-72" data-testid="carriers-search" />
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="carriers-new-btn">
          <Plus className="h-4 w-4 mr-2" /> New carrier
        </Button>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="apex-grid">
          <thead>
            <tr>
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">MC #</th>
              <th className="text-left py-3 px-4">COI</th>
              <th className="text-left py-3 px-4">Contact</th>
              <th className="text-left py-3 px-4">Phone</th>
              <th className="text-left py-3 px-4">Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center mono-label">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center mono-label">No carriers yet — click "New carrier" to add one</td></tr>
            ) : items.map((c, i) => {
              const days = c.coi_days_to_expiry;
              let coiBadge;
              if (!c.coi_expiry_date) {
                coiBadge = <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-zinc-100 text-zinc-700 border-zinc-300">NO COI</span>;
              } else if (days < 0) {
                coiBadge = <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-rose-100 text-rose-900 border-rose-300">EXPIRED</span>;
              } else if (days <= 30) {
                coiBadge = <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-amber-100 text-amber-900 border-amber-300">EXP {days}D</span>;
              } else {
                coiBadge = <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-emerald-100 text-emerald-900 border-emerald-300">VALID</span>;
              }
              return (
              <tr key={c.id} className={`${i % 2 ? "bg-zinc-50" : ""} ${!c.is_active ? "opacity-70" : ""}`} data-testid={`carrier-row-${c.mc_number}`}>
                <td className="py-3 px-4 font-medium">{c.name}</td>
                <td className="py-3 px-4 font-mono">{c.mc_number}</td>
                <td className="py-3 px-4">
                  <div className="flex flex-col gap-1">
                    {coiBadge}
                    {c.coi_expiry_date && <span className="font-mono text-[10px] text-zinc-500">{c.coi_expiry_date}</span>}
                  </div>
                </td>
                <td className="py-3 px-4">{c.contact_name || "—"}</td>
                <td className="py-3 px-4 font-mono text-xs">{c.contact_phone || "—"}</td>
                <td className="py-3 px-4 font-mono text-xs">{c.contact_email || "—"}</td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => { setEditing(c); setOpen(true); }}
                    className="text-[#0056B3] mr-3 inline-flex items-center text-xs"
                    data-testid={`carrier-edit-${c.mc_number}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </button>
                  <button onClick={() => del(c.id, `carrier "${c.name}"`)} className="text-rose-700 inline-flex items-center text-xs"
                    data-testid={`carrier-delete-${c.mc_number}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CarrierFormDialog open={open} onOpenChange={setOpen} carrier={editing} onSaved={load} />
    </div>
  );
}
